import { useMemo } from 'react';
import { WithEnterpriseId, WithServiceId } from '@mews-commander/types';
import { DateTimeService, DateTimeUtcService, useAppClockService } from '@mews/framework';
import { getEnterpriseTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/ducks/enterprises';
import { useResourceCategoriesQuery } from '@mews-commander/legacy-entities/src/queries/resources';
import { useCommanderSelector } from '@mews-commander/utils';
import { FilterFormValues } from '../schemas';
import { useDefaultRateQuery } from './useDefaultRateQuery';

const useTimeUnitsRange = ({ enterpriseId }: WithEnterpriseId) => {
    const timezone = useCommanderSelector((state) => getEnterpriseTimeZoneIdentifier(state, { enterpriseId }));
    const appClockService = useAppClockService();
    const now = DateTimeService.fromUtc(appClockService.getAppNowUtc(), timezone);
    const firstUnitStartDateTime = DateTimeService.startOfDay(now);
    const lastUnitStartDateTime = DateTimeService.addDays(firstUnitStartDateTime, 14);

    return {
        firstUnitStartUtc: DateTimeUtcService.toISOString(DateTimeService.toUtc(firstUnitStartDateTime)),
        lastUnitStartUtc: DateTimeUtcService.toISOString(DateTimeService.toUtc(lastUnitStartDateTime)),
    };
};

export const useDefaultFilterValues = ({ enterpriseId, serviceId }: WithEnterpriseId & WithServiceId) => {
    const defaultRateQuery = useDefaultRateQuery({ enterpriseId, serviceId });
    const resourceCategoriesQuery = useResourceCategoriesQuery({ enterpriseId, serviceId });
    const { firstUnitStartUtc, lastUnitStartUtc } = useTimeUnitsRange({ enterpriseId });

    const allResourceCategoriesIds = useMemo(
        () => resourceCategoriesQuery.data?.map(({ id }) => id),
        [resourceCategoriesQuery.data]
    );

    const values = {
        startDate: firstUnitStartUtc,
        endDate: lastUnitStartUtc,
        view: {
            type: 'rate',
            value: defaultRateQuery.data?.id || '',
        },
        resourceCategories: allResourceCategoriesIds || [],
    } satisfies FilterFormValues;

    return {
        data: values,
        isFetching: defaultRateQuery.isFetching || resourceCategoriesQuery.isFetching,
        isSuccess: defaultRateQuery.isSuccess && resourceCategoriesQuery.isSuccess,
    };
};
